import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Send, X, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const featureFromPath = (path) => {
  const map = {
    '/market-intelligence': 'Market Intelligence',
    '/smart-sourcing': 'Smart Sourcing',
    '/advanced-sourcing': 'Advanced AI Sourcing',
    '/smartmatch': 'SmartMatch',
    '/awards': 'TA Awards',
    '/certifications': 'Certifications',
    '/profile': 'My Profile',
    '/community': 'Community',
    '/events': 'Events',
    '/jobs': 'Jobs',
    '/post-job': 'Post Job',
    '/sourcing-studio': 'Sourcing Studio'
  };
  const hit = Object.keys(map).find((k) => path.startsWith(k));
  return hit ? map[hit] : path || 'General';
};

export const ErrorReporter = () => {
  const [open, setOpen] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);

  const showReporter = useCallback((detail) => {
    setErrorInfo(detail);
    setSubmittedId(null);
    setDescription('');
    setOpen(true);
  }, []);

  useEffect(() => {
    if (window.__errorReporterInstalled) {
      const handler = (e) => showReporter(e.detail);
      window.addEventListener('app-error', handler);
      return () => window.removeEventListener('app-error', handler);
    }
    window.__errorReporterInstalled = true;
    window.__lastErrorReport = {};

    const origFetch = window.fetch.bind(window);
    window.__origFetch = origFetch;

    const maybeReport = (url, status, message) => {
      try {
        const u = String(url);
        if (!u.includes('/api/')) return;
        if (u.includes('/api/support/tickets')) return;
        if (u.includes('/job-status/') || u.includes('/search-result/')) return; // polling endpoints retry themselves
        const key = u.split('?')[0];
        const now = Date.now();
        if (window.__lastErrorReport[key] && now - window.__lastErrorReport[key] < 60000) return;
        window.__lastErrorReport[key] = now;
        window.dispatchEvent(new CustomEvent('app-error', {
          detail: {
            feature: featureFromPath(window.location.pathname),
            page_url: window.location.href,
            error_message: `${message} (${key.replace(BACKEND_URL, '')}${status ? `, HTTP ${status}` : ''})`
          }
        }));
      } catch {}
    };

    window.fetch = async (...args) => {
      try {
        const res = await origFetch(...args);
        if (res.status >= 500) {
          maybeReport(args[0], res.status, 'A server error occurred');
        } else if (res.status === 400 || res.status === 422) {
          // Business/validation failures are still worth reporting
          try {
            const clone = res.clone();
            const body = await clone.json().catch(() => null);
            let detail = body && (body.detail || body.message);
            if (Array.isArray(detail)) detail = detail.map((d) => d.msg || '').join('; ');
            if (detail && typeof detail === 'object') detail = detail.message || JSON.stringify(detail);
            maybeReport(args[0], res.status, detail || 'The request could not be completed');
          } catch {}
        }
        return res;
      } catch (err) {
        maybeReport(args[0], null, 'A network error occurred');
        throw err;
      }
    };

    const handler = (e) => showReporter(e.detail);
    window.addEventListener('app-error', handler);
    return () => window.removeEventListener('app-error', handler);
  }, [showReporter]);

  const submit = async () => {
    if (!errorInfo) return;
    setSubmitting(true);
    try {
      const fetcher = window.__origFetch || window.fetch;
      const token = localStorage.getItem('auth_token');
      const res = await fetcher(`${BACKEND_URL}/api/support/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          feature: errorInfo.feature,
          page_url: errorInfo.page_url,
          error_message: errorInfo.error_message,
          description
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSubmittedId(data.ticket_id);
      }
    } catch {} finally {
      setSubmitting(false);
    }
  };

  if (!open || !errorInfo) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl shadow-2xl border border-red-200 bg-white overflow-hidden animate-in slide-in-from-bottom-4" data-testid="error-reporter-popup">
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white">
        <div className="flex items-center gap-2 font-bold text-sm">
          <AlertTriangle className="w-4 h-4" />
          {submittedId ? 'Issue Reported' : 'Something went wrong'}
        </div>
        <button data-testid="error-reporter-close" onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/20">
          <X className="w-4 h-4" />
        </button>
      </div>

      {submittedId ? (
        <div className="p-4 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
          <p className="font-bold text-slate-900" data-testid="error-reporter-ticket-id">Ticket {submittedId} submitted</p>
          <p className="text-sm text-slate-600 mt-1">Track its status anytime in My Profile → Support Tickets.</p>
          <button onClick={() => setOpen(false)} className="mt-3 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold">Done</button>
        </div>
      ) : (
        <div className="p-4">
          <p className="text-sm text-slate-700 mb-1">
            <strong>{errorInfo.feature}</strong> hit an error. Report it so our team can fix it fast?
          </p>
          <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2 mb-3 break-words">{errorInfo.error_message}</p>
          <textarea
            data-testid="error-reporter-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional: what were you trying to do?"
            rows={2}
            className="w-full text-sm border border-slate-300 rounded-lg p-2 mb-3 focus:ring-2 focus:ring-red-400 outline-none"
          />
          <div className="flex gap-2">
            <button
              data-testid="error-reporter-submit"
              onClick={submit}
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold hover:brightness-110 disabled:opacity-50"
            >
              <Send className="w-3.5 h-3.5" /> {submitting ? 'Sending…' : 'Report Issue'}
            </button>
            <button data-testid="error-reporter-dismiss" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50">Dismiss</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorReporter;
